import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { X, FileText } from "lucide-react";

const STATUS_LABELS = {
  pending: "Pendiente", deposit_paid: "Depósito pagado", confirmed: "Confirmada",
  completed: "Completada", cancelled: "Cancelada",
};
const CHANNEL_LABELS = { online: "Web", partner: "Partner", tienda: "Tienda" };
const EXTRA_LABELS = {
  helmet2: "2º casco", gloves: "2º par de guantes", jacket1: "Chaqueta 1", jacket2: "Chaqueta 2",
  phone_holder: "Soporte de móvil", topcase: "Baúl", full_insurance: "Seguro a todo riesgo", delivery: "Entrega/recogida",
};

const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-100 last:border-0">
    <span className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-faded shrink-0">{label}</span>
    <span data-testid={testid} className="text-sm font-semibold text-topacio-ink text-right">{children}</span>
  </div>
);

// Vista de solo lectura con toda la ficha de una reserva (se abre al pinchar la reserva).
export default function BookingDetailsModal({ bookingRef, headers, onClose }) {
  const [bk, setBk] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/bookings/${bookingRef}`, { headers })
      .then(({ data }) => setBk(data.booking))
      .catch(() => { toast.error("No se pudo cargar la reserva"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingRef]);

  const lc = bk?.license_check || {};
  const unitLabel = bk?.unit_index != null ? `Unidad ${bk.unit_index + 1}` : null;
  const channel = bk?.channel || (bk?.partner_code ? "partner" : "online");

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 p-4" data-testid="booking-details-modal" onClick={onClose}>
      <div className="card-brutal bg-white p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" data-lenis-prevent onClick={(e) => e.stopPropagation()}>
        {!bk ? (
          <p className="text-sm font-bold text-topacio-faded py-6 text-center">Cargando reserva…</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-topacio-ink">Reserva {bk.ref}</h3>
              <button data-testid="booking-details-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[12px] text-topacio-faded font-semibold mb-4">
              {CHANNEL_LABELS[channel] || "Web"}{bk.partner_code ? ` ${bk.partner_code}` : ""} · {STATUS_LABELS[bk.status] || bk.status}
            </p>

            {bk.model_change && (() => {
              const mc = bk.model_change;
              const cfg = {
                pending: { cls: "border-topacio-red bg-topacio-red/5 text-topacio-red", label: "Cambio de moto PENDIENTE de que el cliente acepte" },
                accepted: { cls: "border-topacio-green bg-topacio-green/5 text-topacio-green", label: "Cambio de moto ACEPTADO por el cliente" },
                rejected: { cls: "border-topacio-ink/30 bg-topacio-sand text-topacio-ink", label: "Cambio de moto RECHAZADO por el cliente" },
              }[mc.status] || { cls: "border-topacio-ink/20 bg-white text-topacio-ink", label: "Cambio de moto" };
              return (
                <div data-testid="bd-model-change" className={`mb-4 rounded-xl border-2 ${cfg.cls} p-3`}>
                  <div className="text-[10px] font-extrabold uppercase tracking-wider">{cfg.label}</div>
                  <div className="text-sm font-bold text-topacio-ink mt-0.5">{mc.from_name} → {mc.to_name}</div>
                </div>
              );
            })()}

            <div className="space-y-4">
              <section>
                <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-red mb-1">Cliente</h4>
                <Row label="Nombre" testid="bd-name">{bk.name || "—"}</Row>
                <Row label="Email" testid="bd-email">{bk.email || "—"}</Row>
                <Row label="Teléfono" testid="bd-phone">{bk.phone || "—"}</Row>
                {(lc.type || lc.year || lc.birthdate) && (
                  <Row label="Carnet / edad">{[lc.type, lc.year && `desde ${lc.year}`, lc.birthdate].filter(Boolean).join(" · ") || "—"}</Row>
                )}
              </section>

              <section>
                <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-red mb-1">Moto y fechas</h4>
                <Row label="Moto" testid="bd-bike">{bk.bike_name}{unitLabel ? ` · ${unitLabel}` : ""}</Row>
                <Row label="Recogida" testid="bd-pickup">{bk.start_date} {bk.pickup_time || ""}</Row>
                <Row label="Devolución" testid="bd-return">{bk.end_date} {bk.return_time || ""}</Row>
                <Row label="Días facturables" testid="bd-days">{bk.days}</Row>
                <Row label="Punto de recogida">{bk.pickup === "hotel" ? `Hotel ${bk.hotel_name || ""}` : bk.pickup === "airport" ? "Aeropuerto" : "Oficina"}</Row>
                {Array.isArray(bk.extras) && bk.extras.length > 0 && (
                  <Row label="Extras">{bk.extras.map((e) => EXTRA_LABELS[e] || e).join(", ")}</Row>
                )}
              </section>

              <section>
                <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-red mb-1">Importes</h4>
                <Row label="Alquiler">{bk.rental_total != null ? `${bk.rental_total}€` : "—"}</Row>
                {bk.extras_total > 0 && <Row label="Extras">{bk.extras_total}€</Row>}
                {bk.discount > 0 && <Row label={`Descuento${bk.coupon_code ? ` (${bk.coupon_code})` : ""}`}>-{bk.discount}€</Row>}
                {bk.loyalty_discount > 0 && <Row label="Cliente fiel">-{bk.loyalty_discount}€</Row>}
                <Row label="Total" testid="bd-total"><span className="font-black">{bk.total}€</span></Row>
                {(() => {
                  const total = Number(bk.total) || 0;
                  const advance = bk.advance_amount != null ? Number(bk.advance_amount) : Math.round(total * 0.5);
                  const paid = bk.payment_status === "deposit_paid" ? advance : 0;
                  const remaining = Math.max(0, total - paid);
                  return (
                    <div data-testid="bd-payment-breakdown" className="mt-2 rounded-xl bg-topacio-sand/70 border-2 border-topacio-ink/10 p-3 space-y-1.5">
                      <div className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded mb-0.5">Desglose de pago</div>
                      <div className="flex justify-between text-sm"><span className="font-semibold text-topacio-ink">Anticipo (50%)</span><span data-testid="bd-advance" className="font-extrabold text-topacio-ink">{advance}€</span></div>
                      <div className="flex justify-between text-sm"><span className="font-semibold text-topacio-ink">Pagado</span><span data-testid="bd-paid" className={`font-extrabold ${paid > 0 ? "text-topacio-green" : "text-topacio-faded"}`}>{paid}€{bk.payment_status === "in_store" ? " · en tienda" : ""}</span></div>
                      <div className="flex justify-between text-sm border-t border-topacio-ink/10 pt-1.5"><span className="font-bold text-topacio-ink">Resto pendiente</span><span data-testid="bd-remaining" className={`font-black ${remaining > 0 ? "text-topacio-red" : "text-topacio-green"}`}>{remaining}€</span></div>
                    </div>
                  );
                })()}
                <Row label="Fianza" testid="bd-deposit">{bk.deposit_amount != null ? `${bk.deposit_amount}€` : "—"}{bk.deposit_status ? ` · ${bk.deposit_status}` : ""}</Row>
                {bk.cancellation_fee != null && <Row label="Penalización cancelación">{bk.cancellation_fee}€</Row>}
                {bk.commission_eur != null && <Row label="Comisión partner">{bk.commission_eur}€{bk.commission_paid ? " · cobrada" : " · pendiente"}</Row>}
              </section>

              {bk.message && (
                <section>
                  <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-red mb-1">Observaciones del cliente</h4>
                  <p data-testid="bd-notes" className="text-sm text-topacio-ink bg-topacio-sand/60 rounded-lg p-3 whitespace-pre-wrap">{bk.message}</p>
                </section>
              )}

              <button data-testid="bd-contract-btn" onClick={async () => {
                try {
                  const r = await axios.get(`${API}/admin/bookings/${bk.ref}/contract`, { headers, responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch { toast.error("No se pudo generar el contrato"); }
              }}
                className="btn-secondary w-full !py-2.5 flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" /> Ver contrato PDF
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
